import React, { useState, useEffect } from "react";
import { Table, Button } from "antd";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const MIN_HEIGHT = 260;

/* Soft rank-based colors */
const rankColors = {
  1: "bg-green-50",
  2: "bg-blue-50",
  3: "bg-yellow-50",
};

const ElectionResults = ({surveyType, data = [], onClose }) => {
  const [height, setHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight * 0.45 : 400
  );

  const [isResizing, setIsResizing] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(0);

  /* ---------------- Resize Logic ---------------- */
  const handleMouseDownResize = (e) => {
    e.preventDefault();
    setIsResizing(true);
    setStartY(e.clientY);
    setStartHeight(height);
  };

  useEffect(() => {
    if (!isResizing) return;

    const move = (e) => {
      const delta = startY - e.clientY;
      const max = window.innerHeight - 64;
      setHeight(Math.min(Math.max(startHeight + delta, MIN_HEIGHT), max));
    };

    const up = () => setIsResizing(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [isResizing, startY, startHeight]);

  /* ---------------- Table Columns ---------------- */
  const columns = [
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 70,
      render: (rank) => (
        <span className="font-semibold text-[#123865]">#{rank}</span>
      ),
    },
    {
      title: "Party",
      dataIndex: "party",
      key: "party",
      render: (party) => (
        <span className="font-semibold text-[#0b65c2]">{party}</span>
      ),
    },
    {
      title: "Vote Share",
      dataIndex: "voteShare",
      key: "voteShare",
    },
    {
      title: "Vote %",
      dataIndex: "voteSharePercent",
      key: "voteSharePercent",
      render: (v) => `${v}%`,
    },
    {
      title: "PS Won",
      dataIndex: "psWon",
      key: "psWon",
    },
    surveyType === "parliamentary"
    ? {
        title: "Constituency Won",
        key: "constituencyWon",
        render: (_, r) => r?.constituencies?.constituenciesWonCount || 0,
      }
    : {
        title: "Villages Won",
        key: "villagesWon",
        render: (_, r) => r?.villages?.villagesWonCount || 0,
      },
  ];

  /* ---------------- Render ---------------- */
  return (
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ duration: 0.3 }}
      className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl p-4 z-[50000] flex flex-col"
      style={{ height }}
    >
      {/* Drag Handle */}
      <div
        className="flex justify-center cursor-ns-resize"
        onMouseDown={handleMouseDownResize}
      >
        <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
      </div>

      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-[#123865]">
          Assembly Election Results
        </h3>
        <Button
          type="text"
          icon={<ChevronDown size={22} />}
          onClick={onClose}
        />
      </div>

      {/* Table */}
      <Table
        size="small"
        pagination={false}
        rowKey={(r) => r.party}
        dataSource={data}
        columns={columns}
        rowClassName={(record) =>
          rankColors[record.rank] || "bg-gray-50"
        }
      />
    </motion.div>
  );
};

export default ElectionResults;
